// WHEN A TELEMEDICINE VISIT COUNTS AS ABOUT TO START, and the one place in the repo that decides it.
//
// Plain data and two pure functions of `(Date, Date)` with ZERO imports — no React, no react-intl,
// no clock of their own. Three surfaces need the same line and they must not each have their own:
// `@internal/telemedicine`'s `UpNextCard` and `HomeAppointmentsTile` switch their CTA from
// **Complete forms** to **Join** at it, and `@internal/consumer`'s plan card derives its
// `scheduled-imminent` stage from it (CONAPP-353). A member reading the rail and the plan card side
// by side must not be told two different things about the same visit.
//
// ⚠ HOISTED HERE FROM `@internal/telemedicine/src/appointments/formatting.ts` by CONAPP-353, and
// that is the whole reason it is in this package. `@internal/consumer` depends on
// `@internal/constants` and `@internal/ui` only — it cannot import the telemedicine package — so
// the alternative was a second copy of the threshold, and two definitions of "imminent" drift. The
// semantics did NOT change in the move; see `isAppointmentImminent` on why the post-start case is
// load-bearing.
//
// ⚠ REACHED BY SUBPATH (`@internal/constants/telemedicine/imminence`) and NOT re-exported from
// `../index.ts`, for the same reason `carrot-plan/plan-steps.ts` is: the root re-exports
// `united-states/states.ts`, which imports `react-intl`. Keep this module import-free and keep it
// off the root, or every consumer of a numeric threshold drags an i18n runtime in behind it.

const MINUTE_MS = 60 * 1000

/**
 * How close an appointment has to be before a surface offers `Join` instead of `Complete forms`.
 *
 * Provisional default pending design: frame 4 only draws "In 5 mins" and never states the rule.
 * Kept as a single exported constant so there is one place to change it once design answers — and
 * changing it here moves the rail and the plan card together, which is the point of the hoist.
 */
export const IMMINENT_THRESHOLD_MINUTES = 30

/**
 * True once the appointment is inside the imminent window, INCLUDING AFTER ITS START TIME — a member
 * joining ten minutes late still needs the `Join` action, not a forms prompt.
 *
 * ⚠ THAT POST-START BEHAVIOUR IS DELIBERATE AND DEPENDED ON, not a missing lower bound. The plan
 * card's precedence ladder (`visitStage` in `@internal/consumer`) puts this check ABOVE the
 * outstanding-forms one, so narrowing it to a two-sided window would drop a member who is already
 * late into **Complete forms** at the exact moment they need a way into the call.
 *
 * ⚠ SO THE STALE-PAST CASE IS THE CALLER'S TO CLOSE, AND IT IS NOT CLOSED BY THIS FUNCTION. Nothing
 * here ever stops saying yes. Cancelled, no-show and ended visits are omitted from the BFF's
 * scheduled list — `selectTelemedicineAppointments` drops a `scheduled` visit whose `endTime` has
 * passed — but a client holds the answer it last asked for, not the current one, so the omission
 * only reaches a surface that ASKS AGAIN. `@internal/consumer`'s `useMyAppointments` refetches on
 * focus and on an interval while a visit is booked, for exactly this reason. `useAppointments` in
 * `@internal/telemedicine` does not, and its rail can therefore hold a **Join** for a visit that
 * has ended until something else remounts it: that is a defect in that caller, not a case for a
 * lower bound here.
 *
 * `now` is a REQUIRED PARAMETER rather than a `Date.now()` read, so callers stay pure and testable
 * without fake timers. Both live callers thread a clock they own: `useAppointmentTiming` and the
 * plan page each pass a `now` from `useNow`.
 *
 * ⚠ IT CARRIED A `new Date()` DEFAULT UNTIL THE CONAPP-343 REVIEW, and the default is gone
 * deliberately. A caller that omitted the argument read the WALL CLOCK at call time and got no
 * error for it — the exact impurity the parameter exists to remove, reintroduced silently and one
 * omission at a time. Required is what turns that into a compile error, and this signature is the
 * only place it can be turned into one. Do not put the default back.
 */
export const isAppointmentImminent = (date: Date | null | undefined, now: Date): boolean =>
  date != null && date.getTime() - now.getTime() <= IMMINENT_THRESHOLD_MINUTES * MINUTE_MS

/**
 * The instant the imminent window opens — the appointment's start minus the threshold.
 *
 * Exists so a caller can schedule work *at* the crossing instead of polling towards it (`useNow` in
 * `@internal/ui` takes exactly this). Derived from the same constant as `isAppointmentImminent`, so
 * the two can never disagree about where the line is.
 */
export const getImminentThresholdInstant = (date: Date | null | undefined): Date | null =>
  date == null ? null : new Date(date.getTime() - IMMINENT_THRESHOLD_MINUTES * MINUTE_MS)
